import { useState } from "react";
import {
    ArrowDownIcon,
    ArrowUpIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
    HeartIcon,
    Loader2Icon,
    MessageSquareIcon,
    PencilIcon,
    Trash2Icon,
} from "lucide-react";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Tooltip } from "@component/tooltip";
import {
    ShortlistedCandidateItem,
    ShortlistedCounts,
    ShortlistedSearchType,
} from "@type/candidate";
import { Job } from "@type/job";
import { MetaPagination } from "@type/common";
import { updateShortlistNote } from "@service/job";
import { formatDate } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import { useSnackbar } from "@hook/use-snackbar";

type SortOrder = "newest" | "oldest";

function getInitials(name: string): string {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// True when the BE flagged this entry as a favourite. The mixed `type=all`
// response carries `is_comment` on each item — favourites have `is_comment`
// false/undefined, plain comments have it true.
function isFavouriteEntry(item: ShortlistedCandidateItem): boolean {
    return !item.is_comment;
}

interface ReviewTabContentProps {
    candidateId: string;
    currentUserId: string | undefined;
    currentUserName?: string;
    job: Job | null | undefined;
    isLoading: boolean;
    shortlistedCandidates: ShortlistedCandidateItem[];
    metaPagination: MetaPagination;
    currentPage: number;
    /** Active `type` query passed to /search/shortlisted (default "all"). */
    reviewType: ShortlistedSearchType;
    /** Server-side counts from /shortlisted/counts — drives the chip badges. */
    reviewCounts: ShortlistedCounts;
    /** Called when the user clicks a filter chip — the parent re-fetches. */
    onReviewTypeChange: (type: ShortlistedSearchType) => void;
    onSetCurrentPage: (page: number) => void;
    onMarkAsFavourite: () => void;
    onReload: () => void;
    onDeleteReview: (shortlistId: string) => void;
    /** Current user's profile picture path — used to show their own photo on items. */
    currentUserPhoto?: string;
}

export default function ReviewTabContent({
    currentUserId,
    currentUserName,
    isLoading,
    shortlistedCandidates,
    metaPagination,
    currentPage,
    reviewType,
    reviewCounts,
    onReviewTypeChange,
    onSetCurrentPage,
    onReload,
    onDeleteReview,
    currentUserPhoto,
}: ReviewTabContentProps) {
    const snackbar = useSnackbar();

    const [sortOrder, setSortOrder] = useState<SortOrder>("newest");

    const [editNoteOpen, setEditNoteOpen] = useState(false);
    const [editingShortlist, setEditingShortlist] =
        useState<ShortlistedCandidateItem | null>(null);
    const [editNoteText, setEditNoteText] = useState("");
    const [isSavingNote, setIsSavingNote] = useState(false);

    // BE filters via `type` query, so the feed is whatever the BE returned.
    const sortedFeed = [...shortlistedCandidates].sort((a, b) => {
        const diff =
            new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        return sortOrder === "newest" ? diff : -diff;
    });

    // Server-driven counts so the chip badges stay accurate even when the
    // current `type` filter has narrowed the feed.
    const favouriteCount = reviewCounts.favorites;
    const commentCount = reviewCounts.comments;
    const totalCount = reviewCounts.all;

    // Resolve a profile photo for a card. Own entries use the cached current
    // user photo as a fallback; others use whatever the BE returned.
    const getEntryPhoto = (item: ShortlistedCandidateItem): string | null => {
        if (item.collaborator_logo) return item.collaborator_logo;
        if (currentUserId && item.collaborator_id === currentUserId && currentUserPhoto) {
            return currentUserPhoto;
        }
        return null;
    };

    const openEditNote = (item: ShortlistedCandidateItem) => {
        setEditingShortlist(item);
        setEditNoteText(item.notes || "");
        setEditNoteOpen(true);
    };

    const handleSaveNote = () => {
        if (!editingShortlist?.id) return;
        setIsSavingNote(true);
        updateShortlistNote(editingShortlist.id, editNoteText)
            .then(() => {
                setEditNoteOpen(false);
                setEditingShortlist(null);
                setEditNoteText("");
                snackbar.showSuccess("Note updated.");
                onReload();
            })
            .catch((err) => catchErrorLog(`Failed to update note: ${err}`))
            .finally(() => setIsSavingNote(false));
    };

    const renderAvatar = (photo: string | null, name: string) => {
        if (photo) {
            return (
                <div className="w-9 h-9 overflow-hidden rounded-full border flex-shrink-0">
                    <img
                        className="h-full w-full object-cover"
                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${photo}`}
                        onError={(e) => {
                            e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                            e.currentTarget.onerror = null;
                        }}
                    />
                </div>
            );
        }
        return (
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-semibold border border-gray-200 dark:border-gray-600 flex-shrink-0">
                {getInitials(name)}
            </div>
        );
    };

    const renderCard = (item: ShortlistedCandidateItem, key: string) => {
        const isMine = !!currentUserId && item.collaborator_id === currentUserId;
        const fullName = [item.collaborator_first_name, item.collaborator_last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
        const displayName = isMine
            ? `You (${(currentUserName || fullName).trim()})`
            : fullName || "Unknown";
        const photo = getEntryPhoto(item);
        const favourite = isFavouriteEntry(item);

        return (
            <div
                key={key}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all"
            >
                <div className="flex items-start space-x-3">
                    {renderAvatar(photo, fullName)}

                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                {favourite ? (
                                    <Tooltip
                                        content={
                                            <p>
                                                {isMine
                                                    ? "You favourited this candidate."
                                                    : "This person favourited this candidate."}
                                            </p>
                                        }
                                    >
                                        <HeartIcon className="w-3.5 h-3.5 fill-pink-500 text-pink-500 flex-shrink-0" />
                                    </Tooltip>
                                ) : (
                                    <MessageSquareIcon className="w-3.5 h-3.5 text-blue-500 fill-blue-500 flex-shrink-0" />
                                )}
                                <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                                    {displayName}
                                </span>
                            </div>

                            <div className="flex items-center gap-1 whitespace-nowrap flex-shrink-0">
                                {isMine && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            title="Edit note"
                                            onClick={() => openEditNote(item)}
                                        >
                                            <PencilIcon className="w-3.5 h-3.5 text-gray-400 hover:text-blue-500" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            title={favourite ? "Remove favourite" : "Delete comment"}
                                            onClick={() => onDeleteReview(item.id || "")}
                                        >
                                            <Trash2Icon className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                                        </Button>
                                        <div className="w-px h-3.5 bg-gray-200 dark:bg-gray-700 mx-0.5" />
                                    </>
                                )}
                                <ClockIcon className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-500">
                                    {formatDate(item.created_at)}
                                </span>
                            </div>
                        </div>

                        {item.notes && (
                            <div
                                className={`mt-2 bg-gray-50 dark:bg-gray-900/50 rounded p-2.5 border-l-2 ${
                                    favourite
                                        ? "border-pink-300 dark:border-pink-700"
                                        : "border-blue-300 dark:border-blue-700"
                                }`}
                            >
                                <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                                    "{item.notes}"
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const filterBtn = (
        label: string,
        value: ShortlistedSearchType,
        count: number,
    ) => (
        <button
            key={value}
            onClick={() => onReviewTypeChange(value)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                reviewType === value
                    ? "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
        >
            {label}
            <span
                className={`ml-0.5 ${
                    reviewType === value
                        ? "text-blue-500 dark:text-blue-400"
                        : "text-gray-400 dark:text-gray-500"
                }`}
            >
                {count}
            </span>
        </button>
    );

    return (
        <div className="space-y-4">
            {/* Feed header: title + count + sort */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Team Feedback
                        </span>
                        {!isLoading && (
                            <span className="inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300">
                                {totalCount}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() =>
                            setSortOrder((prev) =>
                                prev === "newest" ? "oldest" : "newest",
                            )
                        }
                        className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                        {sortOrder === "newest" ? (
                            <ArrowDownIcon className="w-3 h-3" />
                        ) : (
                            <ArrowUpIcon className="w-3 h-3" />
                        )}
                        {sortOrder === "newest" ? "Newest" : "Oldest"}
                    </button>
                </div>

                {/* Filter chips drive the BE `type` query — clicking refetches. */}
                <div className="flex items-center gap-1">
                    {filterBtn("All", "all", totalCount)}
                    {filterBtn("Favourites", "favorites", favouriteCount)}
                    {filterBtn("Comments", "comments", commentCount)}
                </div>
            </div>

            {/* Unified feed */}
            <div className="space-y-3">
                {isLoading ? (
                    <div className="w-full min-h-[120px] flex flex-col items-center justify-center">
                        <div className="flex space-x-2 mb-3">
                            <div
                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "0ms" }}
                            />
                            <div
                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "150ms" }}
                            />
                            <div
                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "300ms" }}
                            />
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                            Loading reviews…
                        </p>
                    </div>
                ) : sortedFeed.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center border border-dashed border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {reviewType === "favorites"
                                ? "No team members have favourited this candidate yet."
                                : reviewType === "comments"
                                    ? "No comments yet. Use the box below to share your thoughts."
                                    : "No reviews yet. Favourite this candidate or leave a comment to get started."}
                        </p>
                    </div>
                ) : (
                    sortedFeed.map((item, i) => renderCard(item, `entry-${i}`))
                )}
            </div>

            {/* Pagination */}
            {metaPagination.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[80px] text-center">
                            Page {currentPage} of {metaPagination.total_pages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                onSetCurrentPage(
                                    Math.min(metaPagination.total_pages, currentPage + 1),
                                )
                            }
                            disabled={currentPage === metaPagination.total_pages}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronRightIcon className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Edit note dialog (favourite + comment entries) */}
            <Dialog open={editNoteOpen} onOpenChange={setEditNoteOpen}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Edit your note</DialogTitle>
                        <p className="text-sm text-muted-foreground">
                            Visible to all collaborators on this job.
                        </p>
                    </DialogHeader>
                    <Textarea
                        value={editNoteText}
                        onChange={(e) => setEditNoteText(e.target.value)}
                        rows={4}
                        autoFocus
                        placeholder="Share your thoughts about this candidate..."
                        className="resize-none"
                    />
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => setEditNoteOpen(false)}
                            disabled={isSavingNote}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleSaveNote} disabled={isSavingNote}>
                            {isSavingNote && <Loader2Icon className="w-4 h-4 animate-spin" />}
                            Save note
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
